import React, { useState, useEffect, useCallback, useMemo } from "react";
import {
  Grid,
  Typography,
  Card,
  CardContent,
  FormControlLabel,
  Switch,
  Box,
  Alert,
  Snackbar,
  CircularProgress,
  TextField,
} from "@mui/material";
import { makeStyles } from "@mui/styles";
import { useSelector, useDispatch } from "react-redux";
import { api } from "common";
import { useTranslation } from "react-i18next";
import { colors } from "../components/Theme/WebTheme";
import Button from "components/CustomButtons/Button.js";
import CircularLoading from "../components/CircularLoading";

const useStyles = makeStyles((theme) => ({
  root: {
    padding: theme.spacing(2),
  },
  card: {
    marginBottom: theme.spacing(3),
    borderRadius: 12,
    boxShadow: "0 4px 6px rgba(0, 0, 0, 0.1)",
    border: "1px solid #e0e0e0",
  },
  textField: {
    marginTop: theme.spacing(2),
    width: "100%",
  },
  cardContent: {
    padding: theme.spacing(3),
  },
  title: {
    fontSize: "1.25rem",
    fontWeight: "bold",
    color: "#333",
    marginBottom: theme.spacing(1),
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
  },
  description: {
    fontSize: "0.875rem",
    color: "#666",
    marginTop: theme.spacing(1),
    lineHeight: 1.5,
  },
  switchContainer: {
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    width: "100%",
  },
  switch: {
    marginLeft: theme.spacing(2),
  },
  buttonStyle: {
    margin: theme.spacing(2, 0),
    width: "100%",
    height: 40,
    borderRadius: "30px",
    backgroundColor: colors.MAIN_COLOR,
    color: colors.WHITE,
  },
  loadingContainer: {
    display: "flex",
    justifyContent: "center",
    alignItems: "center",
    padding: theme.spacing(4),
  },
}));

export default function AddOnSettings() {
  const classes = useStyles();
  const { t, i18n } = useTranslation();
  const isRTL = i18n.dir();
  const { editSettings, clearSettingsViewError } = api;
  const settingsdata = useSelector((state) => state.settingsdata);
  const auth = useSelector((state) => state.auth);
  const dispatch = useDispatch();

  // Simple debugging
  console.log("🔍 AddOnSettings Component State:", {
    componentMounted: true,
    settingsdata: !!settingsdata,
    hasSettings: !!settingsdata?.settings,
    auth: !!auth,
    api: !!editSettings,
  });

  // Initialize data state with default values
  const [data, setData] = useState({
    subscription_required: false,
    subscription_required_active: false,
    enable_subscriptions: true,
    manual_withdraw_add_on: false,
    new_booking_history_design: false,
    support_module_enabled: true,
    sos_enabled: false,
    emergency_contact: "+1234567890",
    emergency_message_enabled: false,
    emergency_message_template: "",
    travel_route_addon_enabled: false,
    admin_queue_addon_enabled: false,
    waze_navigation_enabled: false,
    restricted_countries: false,
  });
  const [clicked, setClicked] = useState(false);
  const [localData, setLocalData] = useState({});
  const [optimisticUpdates, setOptimisticUpdates] = useState({});
  const [savingFields, setSavingFields] = useState({});
  const [snackbar, setSnackbar] = useState({
    open: false,
    message: "",
    severity: "success",
  });

  // Memoize initial data to prevent unnecessary re-renders
  const initialData = useMemo(
    () => ({
      admin_queue_addon_enabled: false,
      support_module_enabled: true,
      ...settingsdata.settings,
    }),
    [settingsdata.settings]
  );

  useEffect(() => {
    console.log("🔄 useEffect triggered:", !!settingsdata?.settings);

    if (settingsdata.settings) {
      setData(initialData);
      setLocalData(initialData);
      const hasOptimisticChanges = false;

      if (!hasOptimisticChanges) {
        setData({
          support_module_enabled: true,
          admin_queue_addon_enabled: false,
          ...settingsdata.settings,
        });
      } else {
        setData({
          support_module_enabled: true,
          admin_queue_addon_enabled: false,
          ...settingsdata.settings,
        });
      }
    }
  }, [initialData]);

  // Memoized handlers to prevent re-renders
  const handleSwitchChange = useCallback((e) => {
    const fieldName = e.target.name;
    const newValue = e.target.checked;

    console.log("🔄 Switch changed:", fieldName, "=", newValue);

    // Update local data only - no re-render
    setLocalData((prev) => ({ ...prev, [fieldName]: newValue }));
  }, []);

  const handleTextChange = useCallback(
    (e) => {
      const fieldName = e.target.name;
      const newValue = e.target.value;

      // Update local data only - no re-render
      setLocalData((prev) => {
        // If this is the emergency message template field
        if (fieldName === "emergency_message_template") {
          if (newValue && newValue.trim() !== "") {
            // Has content - automatically enable
            return {
              ...prev,
              [fieldName]: newValue,
              emergency_message_enabled: true,
            };
          } else {
            // Empty - automatically disable
            return {
              ...prev,
              [fieldName]: newValue,
              emergency_message_enabled: false,
            };
          }
        }

        return { ...prev, [fieldName]: newValue };
      });
      // Always save - no permission check
      // dispatch(editSettings(newData)).then(() => {
      //   setOptimisticUpdates(prev => {
      //     const { [fieldName]: removed, ...rest } = prev;
      //     return rest;
      //   });
      //   setSavingFields(prev => {
      //     const { [fieldName]: removed, ...rest } = prev;
      //     return rest;
      //   });

      //   // Show success notification for manual withdraw setting
      //   if (fieldName === 'manual_withdraw_add_on') {
      //     const message = newValue
      //       ? t('manual_withdraw_add_on_enabled') || 'Manual withdraw requests are now enabled for drivers'
      //       : t('manual_withdraw_add_on_disabled') || 'Manual withdraw requests are now disabled for drivers';
      //     setNotification({
      //       open: true,
      //       message: message,
      //       severity: 'success'
      //     });
      //   }
      // }).catch((error) => {
      //   console.error('❌ Error saving setting:', error);
      //   // Revert optimistic update on error
      //   setData(prev => ({ ...prev, [fieldName]: !newValue }));
      //   setOptimisticUpdates(prev => {
      //     const { [fieldName]: removed, ...rest } = prev;
      //     return rest;
      //   });
      //   setSavingFields(prev => {
      //     const { [fieldName]: removed, ...rest } = prev;
      //     return rest;
      //   });

      //   // Show error notification
      //   setNotification({
      //     open: true,
      //     message: `Failed to save setting: ${error.message || 'Unknown error'}`,
      //     severity: 'error'
      //   });
      // });
    },
    [
      // dispatch, t
    ]
  );

  // Show snackbar function like AdminQueue
  const showSnackbar = (message, severity = "success") => {
    setSnackbar({ open: true, message, severity });
  };

  const handleSubmit = useCallback(
    async (e) => {
      e.preventDefault();

      try {
        console.log("🔄 Saving addon settings configuration...");

        // Check permission first
        if (!data.AllowCriticalEditsAdmin) {
          console.log("❌ No permission to edit settings");
          showSnackbar(
            t("demo_mode") || "Demo mode - settings cannot be saved",
            "warning"
          );
          return;
        }

        // Check if there are changes to save
        if (Object.keys(localData).length === 0) {
          console.log("ℹ️ No changes to save");
          showSnackbar(t("no_changes_to_save") || "No changes to save", "info");
          return;
        }

        // Start loading
        setClicked(true);

        // Merge local changes with original data
        const finalData = { ...data, ...localData };

        console.log("💾 Saving addon settings:", finalData);

        // Call API - if no error thrown, consider it success
        await dispatch(editSettings(finalData));

        console.log("✅ Addon settings saved successfully");
        setClicked(false);
        setLocalData({}); // Clear local changes

        // Show success toast like AdminQueue
        showSnackbar(
          t("admin_queue_success_configuration_updated") ||
            "Configuration updated successfully",
          "success"
        );
      } catch (error) {
        console.error("❌ Error saving addon settings:", error);
        setClicked(false);
        showSnackbar(
          t("update_failed") || `Error: ${error.message || "Save failed"}`,
          "error"
        );
      }
    },
    [data, localData, dispatch, t]
  );

  const handleClose = useCallback(() => {
    setClicked(false);
  }, []);

  // Memoized display data to prevent re-renders
  const displayData = useMemo(() => {
    return { ...data, ...localData };
  }, [data, localData]);

  if (!settingsdata.settings) {
    console.log("⏳ No settings data available, showing loading...");
    return (
      <div className={classes.loadingContainer}>
        <CircularLoading />
      </div>
    );
  }

  // Simple render debugging
  console.log("🎨 Rendering AddOnSettings:", {
    hasData: !!data,
    dataKeys: Object.keys(data || {}),
    manualWithdrawValue: data?.manual_withdraw_add_on,
  });

  return (
    <div className={classes.root}>
      <Typography
        variant="h4"
        component="h1"
        gutterBottom
        style={{ textAlign: isRTL === "rtl" ? "right" : "left" }}
      >
        {t("add_on_settings")}
      </Typography>

      <Typography
        variant="body1"
        color="textSecondary"
        gutterBottom
        style={{ textAlign: isRTL === "rtl" ? "right" : "left" }}
      >
        {t("add_on_settings_description")}
      </Typography>

      <form onSubmit={handleSubmit}>
        <Grid container spacing={3}>
          {/* Left Column - 4 cards */}
          <Grid item xs={12} md={6}>
            {/* New Booking History Design Add-on Card */}
            <Card className={classes.card}>
              <CardContent className={classes.cardContent}>
                <div className={classes.switchContainer}>
                  <div>
                    <Typography className={classes.title}>
                      {t("new_booking_history_design_title") ||
                        "New Booking History Design"}
                    </Typography>
                    <Typography className={classes.description}>
                      {t("new_booking_history_design_desc") ||
                        "Enable the redesigned booking history interface with enhanced filters, improved layout, and trip details view. Disable to use the original design."}
                    </Typography>
                  </div>
                  <Switch
                    className={classes.switch}
                    checked={displayData.new_booking_history_design}
                    onChange={handleSwitchChange}
                    name="new_booking_history_design"
                    color="primary"
                    disabled={false}
                  />
                </div>
              </CardContent>
            </Card>

            {/* Support Module Card */}
            <Card className={classes.card}>
              <CardContent className={classes.cardContent}>
                <div className={classes.switchContainer}>
                  <div>
                    <Typography className={classes.title}>
                      {t("support_module_title") || "Support Module"}
                    </Typography>
                    <Typography className={classes.description}>
                      {t("support_module_desc") ||
                        "Enable customer support functionality including support requests, categories management, and customer support tickets. Users can submit support requests from mobile app."}
                    </Typography>
                  </div>
                  <Switch
                    className={classes.switch}
                    checked={displayData.support_module_enabled}
                    onChange={handleSwitchChange}
                    name="support_module_enabled"
                    color="primary"
                    disabled={false}
                  />
                </div>
              </CardContent>
            </Card>
          </Grid>

          {/* Right Column - 3 cards */}
          <Grid item xs={12} md={6}>
            {/* Admin Queue Add-On Card */}
            <Card className={classes.card}>
              <CardContent className={classes.cardContent}>
                <div className={classes.switchContainer}>
                  <div>
                    <Typography className={classes.title}>
                      {t("admin_queue_addon_enable") || "Admin Queue System"}
                    </Typography>
                    <Typography className={classes.description}>
                      {t("admin_queue_addon_enable_description") ||
                        "Enable admin queue system to manage driver assignments and automate the process."}
                    </Typography>
                  </div>
                  <Switch
                    className={classes.switch}
                    checked={displayData.admin_queue_addon_enabled}
                    onChange={handleSwitchChange}
                    name="admin_queue_addon_enabled"
                    color="primary"
                  />
                </div>
              </CardContent>
            </Card>
          </Grid>

          {/* Save Button Section - Full Width */}
          <Grid item xs={12}>
            <Box>
              <Typography
                variant="h6"
                gutterBottom
                style={{ textAlign: isRTL === "rtl" ? "right" : "left" }}
              >
                {t("settings_info")}
              </Typography>
              <Typography
                variant="body2"
                color="textSecondary"
                paragraph
                style={{ textAlign: isRTL === "rtl" ? "right" : "left" }}
              >
                {t("add_on_settings_info")}
              </Typography>

              <Button
                size="lg"
                onClick={handleSubmit}
                variant="contained"
                color="success"
                className={classes.buttonStyle}
                disabled={
                  !data.AllowCriticalEditsAdmin ||
                  Object.keys(localData).length === 0
                }
              >
                {clicked ? (
                  <>
                    <CircularProgress
                      size={20}
                      style={{ marginRight: 8, color: "white" }}
                    />
                    {t("saving")}
                  </>
                ) : (
                  <>{t("save_settings")}</>
                )}
              </Button>
            </Box>
          </Grid>
        </Grid>
      </form>

      {/* AlertDialog removed - using snackbar notifications only */}

      {/* Success Notification */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={6000}
        onClose={() => setSnackbar({ ...snackbar, open: false })}
      >
        <Alert
          onClose={() => setSnackbar({ ...snackbar, open: false })}
          severity={snackbar.severity}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </div>
  );
}
